// Library imports
import React, { useMemo, useRef, useState } from "react";

// User Component imports
import { PROMPT_TABS } from "../utils/promptVersions";
import { computeDiffOps, collapseDiffOps, pairDiffRows, diffRowStats, normalizeNewlines } from "../utils/promptDiff";

// 差分の前後に残す行数
const DIFF_CONTEXT = 3;
// 差分ビューで計算を打ち切る編集距離（行数）
const DIFF_MAX_EDIT_LINES = 10000;
// 変更箇所へ移動したとき、上に残す余白（px）
const HUNK_SCROLL_MARGIN = 48;

/**
 * 2つの本文の行差分（大きすぎて打ち切った場合は ops が null）と、表示行・変更のかたまり数・種類ごとの行数
 */
export const useDiffOps = (leftText, rightText) => useMemo(() => {
    const ops = computeDiffOps(normalizeNewlines(leftText), normalizeNewlines(rightText), { maxEditLength: DIFF_MAX_EDIT_LINES });
    const { rows, hunks } = pairDiffRows(ops || []);
    return { ops, count: ops ? ops.filter((op) => op.type !== 'eq').length : null, rows, hunks, stat: diffRowStats(rows) };
}, [leftText, rightText]);

/**
 * 変更のかたまりを前後に移動する（端で反対側へ循環）。scrollRef は PromptDiffView のスクロール領域に付ける
 * resetKey（表示中のタブ）が変わったら先頭のかたまりに戻る。まだ動かしていなければ「次」は先頭のかたまりへ移動する
 */
export const useDiffNav = (hunks, resetKey) => {
    const [pos, setPos] = useState({ key: resetKey, cur: 0, moved: false });
    const scrollRef = useRef(null);
    if (pos.key !== resetKey) setPos({ key: resetKey, cur: 0, moved: false });
    const fresh = pos.key !== resetKey;
    const cur = fresh ? 0 : Math.min(pos.cur, Math.max(0, hunks - 1));
    const moved = !fresh && pos.moved;
    const go = (i) => {
        if (!hunks) return;
        const next = ((i % hunks) + hunks) % hunks;
        setPos({ key: resetKey, cur: next, moved: true });
        const box = scrollRef.current;
        const el = box && box.querySelector('[data-hunk="' + next + '"]');
        if (el) box.scrollTo({ top: Math.max(0, el.offsetTop - HUNK_SCROLL_MARGIN), behavior: 'smooth' });
    };
    return { cur, hunks, scrollRef, prev: () => go(cur - 1), next: () => go(moved ? cur + 1 : cur) };
};

export const diffCountLabel = (count) => (count === null ? "差分が大きすぎるため表示できません" : (count ? count + " 行の差分" : "差分はありません"));

/**
 * 差分ヘッダーの行数（追加・削除・書き換え）と変更箇所の移動。差分が無いときは出さない
 */
export const DiffNav = ({ stat, nav }) => {
    if (!nav.hunks) return null;
    return (
        <>
            <div className="ops-diff-stat">
                <span className="is-add" title="追加した行">+{stat.add}</span>
                <span className="is-del" title="削除した行">−{stat.del}</span>
                <span className="is-chg" title="書き換えた行">~{stat.chg}</span>
            </div>
            <div className="ops-diff-nav">
                <button type="button" className="ops-diff-nav-btn" title="前の変更" onClick={nav.prev}><i className="bi bi-chevron-up" /></button>
                <span className="ops-diff-nav-label">変更 {nav.cur + 1} / {nav.hunks}</span>
                <button type="button" className="ops-diff-nav-btn" title="次の変更" onClick={nav.next}><i className="bi bi-chevron-down" /></button>
            </div>
        </>
    );
};

/**
 * 差分ヘッダーの切替（システム／ユーザー、差分のみ／全文）
 * counts を渡すとタブにタブごとの差分行数を添える
 */
export const DiffToggles = ({ tab, onChangeTab, fullText, onChangeFullText, counts }) => (
    <>
        <div className="ops-diff-toggle">
            {PROMPT_TABS.map((t) => {
                const count = counts ? counts[t.key] : undefined;
                return (
                    <button key={t.key} type="button" className={"ops-diff-mode" + (tab === t.key ? " is-active" : "")} onClick={() => onChangeTab(t.key)}>
                        {t.label}{count ? <span className="ops-diff-mode-count">{count}</span> : null}
                    </button>
                );
            })}
        </div>
        <div className="ops-diff-toggle">
            {[{ key: false, label: "差分のみ" }, { key: true, label: "全文" }].map((m) => (
                <button key={String(m.key)} type="button" className={"ops-diff-mode" + (fullText === m.key ? " is-active" : "")} onClick={() => onChangeFullText(m.key)}>{m.label}</button>
            ))}
        </div>
    </>
);

// 片側の本文。塗るかけらだけ span で囲む
const DiffText = ({ segs }) => (
    <div className="ops-diff-text">
        {segs.map((g, i) => (g.hl ? <span key={i} className="ops-diff-hl">{g.t}</span> : g.t))}
    </div>
);

// 行の左右に出す本文のかけら
const rowSegments = (row) => {
    if (row.type === 'chg') return row.segs;
    if (row.type === 'del') return { left: [{ t: row.left, hl: true }], right: [] };
    if (row.type === 'add') return { left: [], right: [{ t: row.right, hl: true }] };
    return { left: [{ t: row.left, hl: false }], right: [{ t: row.right, hl: false }] };
};

/**
 * 左右2列の差分表示。書き換え行は同じ高さに並べ、変わった文字だけを塗る
 * 差分が無いときは空状態から全文表示に切り替えられる。省略行はクリックで展開（展開はタブごと）
 */
const PromptDiffView = ({ ops, rows, nav, tab, leftTitle, rightTitle, fullText, onShowFull }) => {
    const [expanded, setExpanded] = useState(() => new Set());
    const items = useMemo(() => (
        fullText ? rows.map((row, index) => ({ type: 'op', op: row, index })) : collapseDiffOps(rows, DIFF_CONTEXT)
    ), [rows, fullText]);
    const isEmpty = !!ops && !nav.hunks && !fullText;

    const expandKey = (start) => tab + ":" + start;
    const expand = (start) => setExpanded((prev) => new Set(prev).add(expandKey(start)));

    const renderRow = (row, index) => {
        const segs = rowSegments(row);
        return (
            <div key={index} className={"ops-diff-line is-" + row.type} data-hunk={row.hunk}>
                {row.hunk !== undefined && row.hunk === nav.cur && <div className="ops-diff-cur" />}
                <div className="ops-diff-half is-left">
                    <div className="ops-diff-ln">{row.lnL ?? ""}</div>
                    <div className="ops-diff-sign">{row.type === 'chg' || row.type === 'del' ? "−" : ""}</div>
                    <DiffText segs={segs.left} />
                </div>
                <div className="ops-diff-half is-right">
                    <div className="ops-diff-ln">{row.lnR ?? ""}</div>
                    <div className="ops-diff-sign">{row.type === 'chg' || row.type === 'add' ? "+" : ""}</div>
                    <DiffText segs={segs.right} />
                </div>
            </div>
        );
    };

    return (
        <>
            <div className="ops-diff-cols">
                <div className="ops-diff-col-title">{leftTitle}</div>
                <div className="ops-diff-col-title">{rightTitle}</div>
            </div>
            {isEmpty &&
                <div className="ops-diff-empty">
                    <i className="bi bi-check2-circle" />
                    <div className="ops-diff-empty-title">{PROMPT_TABS.find((t) => t.key === tab)?.label ?? ""}に差分はありません</div>
                    <div className="ops-diff-empty-sub">左右の内容は同じです。もう一方のプロンプトに切り替えるか、全文で確認できます。</div>
                    <button type="button" className="ops-diff-empty-btn" onClick={onShowFull}>全文を表示</button>
                </div>
            }
            {!ops && <div className="ops-diff-none">差分が大きすぎるため表示できません</div>}
            {!isEmpty &&
                <div className="ops-diff-scroll" ref={nav.scrollRef}>
                    {items.flatMap((item) => {
                        if (item.type === 'op') return [renderRow(item.op, item.index)];
                        if (expanded.has(expandKey(item.start))) {
                            return rows.slice(item.start, item.start + item.count).map((row, i) => renderRow(row, item.start + i));
                        }
                        return [
                            <button key={"s" + item.start} type="button" className="ops-diff-gap" title="クリックで展開" onClick={() => expand(item.start)}>
                                <i className="bi bi-arrows-expand" />{item.count} 行（変更なし）を表示
                            </button>
                        ];
                    })}
                </div>
            }
        </>
    );
};

export default PromptDiffView;
